import { UserData } from "../context/UserContext";

const API_ROOT = process.env.REACT_APP_BASE_API + '/';

export const ASSUMPTION_REFUSED_EVENT = 'neoni:assumption-refused';
export const SESSION_EXPIRED_EVENT = 'neoni:session-expired';
export const ACCESS_AUDIT_UNAVAILABLE_EVENT = 'neoni:access-audit-unavailable';
export const READ_ONLY_ACCESS_EVENT = 'neoni:read-only-access';
export const ACCOUNT_LOCKED_EVENT = 'neoni:account-locked';

// COMPANY_LOCKED is excluded: the organisation picker and dashboard banner already handle it.
export const ACCOUNT_LOCK_CODES = ['ADMIN_LOCKED', 'USER_LOCKED'];

// Serializable error class to avoid Redux non-serializable warnings
class SerializableApiError extends Error {
    status?: number;
    statusText?: string;
    body?: any;

    constructor(message: string, status?: number, statusText?: string, body?: any) {
        super(message);
        this.name = 'SerializableApiError';
        this.status = status;
        this.statusText = statusText;
        this.body = body;
    }
}

function buildApi(getUserData: () => UserData | undefined) {
    const defaultHeaders = () => {
        const userData = getUserData();
        const headers: Record<string, string> = {};
        const assumedCompanyType = userData?.access?.assumedCompanyType;
        if(assumedCompanyType && userData?.companyId) {
            headers[`neoni-${assumedCompanyType}-assumed-company-id`] = userData.companyId;
        }
        return headers;
    }
    
    const request = async (path: string, method: string, body?: any) => {

        // remove leading slashes on path
        path = path.replace(/^\/+/, '');
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...defaultHeaders(),
        };

        const response = await fetch(
            API_ROOT + path,
            {
                method,
                body: JSON.stringify(body),
                headers,
            }
        );

        if(!response.ok) {
            console.log('Response not OK')
            let responseBody;
            try {
                responseBody = await response.json();
                if(responseBody.redirectTo) {
                    console.log('Redirecting')
                    window.location = responseBody.redirectTo;
                }
            } catch(_) {
                // If response is not JSON, continue with basic error
            }
            
            // Throw serializable error
            const error = new SerializableApiError(
                'HTTP error',
                response.status,
                response.statusText,
                responseBody
            );
            if(response.status === 403 && responseBody?.code === 'ASSUMPTION_NOT_PERMITTED') {
                // UserProvider drops the assumption and navigates away; never settling keeps the
                // page that made this call from handling (or crashing on) the refusal.
                window.dispatchEvent(new Event(ASSUMPTION_REFUSED_EVENT));
                return new Promise(() => {});
            }
            if(response.status === 403 && responseBody?.code === 'SESSION_EXPIRED') {
                // UserProvider returns the user to the login screen; the caller must not render an error.
                window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
                return new Promise(() => {});
            }
            if(response.status === 403 && ACCOUNT_LOCK_CODES.includes(responseBody?.code)) {
                // UserProvider signs the user out and returns them to login with an on-hold message.
                window.dispatchEvent(new Event(ACCOUNT_LOCKED_EVENT));
                return new Promise(() => {});
            }
            if(response.status === 503 && responseBody?.code === 'ACCESS_AUDIT_UNAVAILABLE') {
                // Decision D8 (docs/todo/user-org-membership-i-fixes-B.md): distinct from
                // ASSUMPTION_NOT_PERMITTED - the assumption itself is still valid, so UserProvider
                // only notifies here, it does not clear the assumption or navigate away.
                window.dispatchEvent(new Event(ACCESS_AUDIT_UNAVAILABLE_EVENT));
                return new Promise(() => {});
            }
            if(response.status === 403 && responseBody?.code === 'READ_ONLY_ACCESS') {
                // View-only delegated access: notify only, the assumption stays valid.
                window.dispatchEvent(new Event(READ_ONLY_ACCESS_EVENT));
                return new Promise(() => {});
            }
            throw error;
        }
        const result = await response.json();
        return result;
    };

    const get = async (path: string) => {
        return request(path, 'GET');
    };

    const post = async (path: string, body: any) => {
        return request(path, 'POST', body);
    };

    const put = async (path: string, body: any) => {
        return request(path, 'PUT', body)
    };

    const del = async(path: string, body: any) => {
        return request(path, 'DELETE', body)
    };

    const form = async(path: string, rawBody: any) => {

        const response = await fetch(
            API_ROOT + path,
            {
                method: 'POST',
                body: rawBody,
                headers: defaultHeaders(),
            }
        );

        const result = await response.json();
        return result;
    }

    return {
        request,
        get,
        post,
        put,
        delete: del,
        form,
    };
}

export type NeoniApi = ReturnType<typeof buildApi>;

export default buildApi;
